/**
 * r16q7 (Live mode, docs/LIVE-MODE-DESIGN.md section 7): the Inbox's Live tick. The server keeps the session; this reads it every 2 s while it's running and keeps its chat the
 * same as the chat open in the browser.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { chatsApi } from './chatsApi';

const RUNNING = ['REQUESTED', 'STARTING', 'READY', 'ENDING'];

/** Pure: is this session still running? */
export const liveRunning = (s) => !!s && RUNNING.includes(s.state);

/** Pure: the words beside the tick. */
export function liveWords(s) {
  if (!s) return '';
  switch (s.state) {
    case 'REQUESTED': return 'Stopping automation on this phone';
    case 'STARTING': return 'Opening Instagram on this account';
    case 'READY': return 'Live';
    case 'ENDING': return 'Ending Live, automation comes back';
    default: break;
  }
  if (s.note) return s.note;
  const why = {
    user: 'Live ended. Automation is back on this phone.',
    idle: `Live ended after ${s.idleMinutes || 10} idle minutes. Automation is back on this phone.`,
    pc_lost: 'The PC stopped answering, so Live ended.',
    no_worker: 'No worker took it: is this PC\'s client running, with this slot\'s phone on the latest worker?',
    start_failed: 'The phone didn\'t get to Instagram in time, so Live ended.',
    worker_failed: 'The worker couldn\'t start Live on this phone.',
    worker_ended: 'Live ended. Automation is back on this phone.',
    switched: 'Live moved to another slot on this phone.',
  };
  return why[s.reason] || 'Live ended.';
}

/** Pure: minutes and seconds for the idle warning. */
export const liveLeftText = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

export function useChatLive(slot, threadId) {
  const qc = useQueryClient();
  const key = ['chat-live', slot];
  const q = useQuery({
    queryKey: key,
    queryFn: () => chatsApi.liveGet(slot).then((r) => r.data),
    enabled: !!slot,
    retry: false,
    refetchInterval: (query) => (liveRunning(query.state.data && query.state.data.session) ? 2000 : 15000),
  });
  const session = (q.data && q.data.session) || null;
  const enabled = !!(q.data && q.data.enabled);
  const running = liveRunning(session);
  const ready = !!session && session.state === 'READY';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const put = useCallback((data) => qc.setQueryData(['chat-live', slot], data), [qc, slot]);

  // the chat the phone follows is the chat open here
  const asked = useRef(null);
  const sessionId = session && session.id;
  const sessionThread = session && session.threadId;
  useEffect(() => {
    if (!running || !threadId || sessionThread === threadId || asked.current === threadId) return;
    asked.current = threadId;
    chatsApi.liveFocus(sessionId, threadId).then((r) => put(r.data)).catch(() => {}).finally(() => { asked.current = null; });
  }, [running, threadId, sessionId, sessionThread, put]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await chatsApi.liveStart(slot, threadId);
      put(r.data);
    } catch (e) {
      setError('Live could not start. Try again.');
    } finally {
      setBusy(false);
    }
  }, [slot, threadId, put]);

  const stop = useCallback(async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await chatsApi.liveEnd(sessionId);
      put(r.data);
    } catch (e) {
      setError('Live could not be ended. Try again.');
    } finally {
      setBusy(false);
    }
  }, [sessionId, put]);

  const lastActivity = useRef(0);
  /** "Still here": typing and sending, at most every 30 s; Keep Live passes force. */
  const activity = useCallback((force) => {
    const now = Date.now();
    if (!sessionId || !running || (!force && now - lastActivity.current < 30000)) return;
    lastActivity.current = now;
    chatsApi.liveActivity(sessionId).then((r) => put(r.data)).catch(() => {});
  }, [sessionId, running, put]);

  return { enabled, session, running, ready, busy, error, start, stop, activity };
}
